import { supabase } from './supabase';

export interface RegisteredPushDevice {
  id: string;
  endpointPreview: string;
  endpoint?: string;
  deviceLabel: string;
  platform: 'android' | 'ios' | 'desktop' | 'web';
  browser: string;
  isStandalonePwa: boolean;
  isActive: boolean;
  lastDeliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserPushStatusResponse {
  success: boolean;
  configured: boolean;
  vapidKeySource?: 'env' | 'ephemeral';
  userId: string;
  pushEnabled: boolean;
  manuallyUnsubscribed?: boolean;
  activeDeviceCount: number;
  devices: RegisteredPushDevice[];
}

export type BrowserPushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

const STORAGE_MANUAL_UNSUB_PREFIX = 'jobmatch_push_manual_unsub_';
const STORAGE_PREF_DISABLED_PREFIX = 'jobmatch_push_pref_disabled_';
const STORAGE_PROMPT_DISMISSED_PREFIX = 'jobmatch_push_prompt_dismissed_';

export function isDeviceManuallyUnsubscribed(userId: string): boolean {
  if (!userId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(`${STORAGE_MANUAL_UNSUB_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function setDeviceManuallyUnsubscribed(userId: string, unsubscribed: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const key = `${STORAGE_MANUAL_UNSUB_PREFIX}${userId}`;
    if (unsubscribed) {
      window.localStorage.setItem(key, '1');
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

export function isLocalPushPreferenceDisabled(userId: string): boolean {
  if (!userId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(`${STORAGE_PREF_DISABLED_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function setLocalPushPreferenceDisabled(userId: string, disabled: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const key = `${STORAGE_PREF_DISABLED_PREFIX}${userId}`;
    if (disabled) {
      window.localStorage.setItem(key, '1');
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

export function isPushPromptDismissed(userId: string): boolean {
  if (!userId || typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(`${STORAGE_PROMPT_DISMISSED_PREFIX}${userId}`) === '1' ||
      window.localStorage.getItem(`${STORAGE_PROMPT_DISMISSED_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function setPushPromptDismissed(userId: string, dismissed: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const key = `${STORAGE_PROMPT_DISMISSED_PREFIX}${userId}`;
    if (dismissed) {
      window.sessionStorage.setItem(key, '1');
      window.localStorage.setItem(key, '1');
    } else {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

export function emitPushStatusUpdated(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent('jobmatch:push-status-updated', { detail: { userId } })
    );
  } catch {
    // Ignore
  }
}

/**
 * Detects whether the user is on an iPhone / iPad / iPod running iOS/iPadOS.
 */
export function isIosDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOS;
}

/**
 * Detects whether the app is running as an installed Home Screen web app (standalone mode).
 * iPhone users on iOS 16.4+ MUST install JobMatch NG on their Home Screen before Web Push is available.
 */
export function isStandaloneWebApp(): boolean {
  if (typeof window === 'undefined') return false;
  const isDisplayStandalone =
    window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const isNavigatorStandalone = Boolean((window.navigator as any)?.standalone);
  return Boolean(isDisplayStandalone || isNavigatorStandalone);
}

/**
 * Checks whether the current browser supports Service Workers, PushManager, and Notification API.
 */
export function isWebPushSupportedInBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Returns the current browser Notification permission state without prompting the user.
 */
export function getBrowserPushPermissionState(): BrowserPushPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission as BrowserPushPermissionState;
}

/**
 * Converts a URL-safe base64 VAPID public key string to a Uint8Array for pushManager.subscribe().
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Builds authorization headers including the current Supabase session access token if available.
 */
async function getAuthHeaders(userId?: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userId) {
    headers['x-user-id'] = userId;
  }
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // Fallback to x-user-id when using demo/local session
  }
  return headers;
}

/**
 * Registers `/sw.js` and waits for the active service worker registration.
 * Does NOT request notification permission.
 */
export async function ensurePushServiceWorkerRegistered(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    await navigator.serviceWorker.ready;
    return registration;
  } catch (err) {
    console.warn('[WebPushClient] Service Worker registration warning:', err);
    return null;
  }
}

/**
 * Returns the current browser's active PushSubscription endpoint if subscribed, or null.
 */
export async function getCurrentBrowserPushEndpoint(): Promise<string | null> {
  if (!isWebPushSupportedInBrowser()) return null;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration || !registration.pushManager) return null;
    const sub = await registration.pushManager.getSubscription();
    return sub?.endpoint || null;
  } catch {
    return null;
  }
}

/**
 * Builds a friendly human-readable label for the current device.
 */
export function buildDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Web Browser';
  const ua = navigator.userAgent || '';
  const standalone = isStandaloneWebApp();
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return standalone ? 'iPhone Home Screen App' : 'iPhone Safari';
  }
  if (/Android/i.test(ua)) {
    if (/EdgA/i.test(ua)) return 'Android Edge';
    if (/Firefox/i.test(ua)) return 'Android Firefox';
    return standalone ? 'Android Home Screen App' : 'Android Chrome';
  }
  if (/Macintosh|Mac OS X/i.test(ua)) {
    if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) return 'Mac Chrome';
    if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Mac Safari';
    if (/Firefox/i.test(ua)) return 'Mac Firefox';
    return 'Mac Browser';
  }
  if (/Windows/i.test(ua)) {
    if (/Edg/i.test(ua)) return 'Windows Edge';
    if (/Chrome/i.test(ua)) return 'Windows Chrome';
    if (/Firefox/i.test(ua)) return 'Windows Firefox';
    return 'Windows Browser';
  }
  return 'Web Browser';
}

/**
 * Fetches the user's server-side Web Push status and registered devices.
 */
export async function fetchUserPushStatus(userId: string): Promise<UserPushStatusResponse | null> {
  if (!userId) return null;
  try {
    const headers = await getAuthHeaders(userId);
    const res = await fetch(`/api/push/status?userId=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers,
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data as UserPushStatusResponse;
  } catch {
    return null;
  }
}

/**
 * Subscribes the current browser/device to JobMatch NG Web Push notifications.
 * Only requests browser notification permission when `autoRefresh` is false (explicit user click).
 */
export async function subscribeCurrentDeviceToPush(params: {
  userId: string;
  role: 'customer' | 'professional';
  email?: string;
  autoRefresh?: boolean;
}): Promise<{
  success: boolean;
  skipped?: boolean;
  permission: BrowserPushPermissionState;
  endpoint?: string;
  status?: UserPushStatusResponse | null;
  error?: string;
}> {
  const { userId, role, email, autoRefresh = false } = params;

  if (!userId) {
    return {
      success: false,
      permission: getBrowserPushPermissionState(),
      error: 'Please sign in to enable push notifications.',
    };
  }

  // Check iPhone Home Screen requirement first
  if (isIosDevice() && !isStandaloneWebApp()) {
    return {
      success: false,
      permission: getBrowserPushPermissionState(),
      error:
        'On iPhone (iOS 16.4+), please tap the Share button in Safari and select "Add to Home Screen", then open JobMatch NG from your Home Screen to enable push notifications.',
    };
  }

  if (!isWebPushSupportedInBrowser()) {
    return {
      success: false,
      permission: 'unsupported',
      error: 'Web Push notifications are not supported in this browser or environment.',
    };
  }

  // 1. Only request permission when explicitly triggered by user click (!autoRefresh)
  let permission: NotificationPermission = Notification.permission;
  if (!autoRefresh && permission === 'default') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return {
      success: false,
      permission: permission as BrowserPushPermissionState,
      error:
        permission === 'denied'
          ? 'Browser notification permission is blocked. Please allow notifications for jobmatch.ng in your browser site settings.'
          : 'Notification permission was not granted.',
    };
  }

  // If explicit user action, clear manual unsubscribe and disabled flags
  if (!autoRefresh) {
    setDeviceManuallyUnsubscribed(userId, false);
    setLocalPushPreferenceDisabled(userId, false);
    setPushPromptDismissed(userId, false);
  }

  try {
    // 2. Register Service Worker
    const registration = await ensurePushServiceWorkerRegistered();
    if (!registration || !registration.pushManager) {
      return {
        success: false,
        permission: 'granted',
        error: 'Unable to initialize Service Worker PushManager.',
      };
    }

    // 3. Fetch server VAPID public key
    const keyRes = await fetch('/api/push/vapid-public-key');
    const keyData = await keyRes.json();
    if (!keyRes.ok || !keyData?.publicKey) {
      return {
        success: false,
        permission: 'granted',
        error: keyData?.error || 'VAPID public key could not be retrieved from server.',
      };
    }

    const applicationServerKey = urlBase64ToUint8Array(keyData.publicKey);

    // 4. Subscribe or reuse existing PushManager subscription
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as unknown as BufferSource,
      });
    }

    const subJson = subscription.toJSON();
    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
      return {
        success: false,
        permission: 'granted',
        error: 'Browser returned an incomplete push subscription.',
      };
    }

    // 5. Register or refresh subscription on the backend
    const headers = await getAuthHeaders(userId);
    const subRes = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId,
        role,
        email,
        autoRefresh,
        subscription: {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
        },
        deviceLabel: buildDeviceLabel(),
        userAgent: navigator.userAgent,
        isStandalonePwa: isStandaloneWebApp(),
      }),
    });

    const subResult = await subRes.json();
    if (subResult?.skipped) {
      if (subResult.reason === 'user_preference_disabled') {
        setLocalPushPreferenceDisabled(userId, true);
      } else if (subResult.reason === 'manually_unsubscribed') {
        setDeviceManuallyUnsubscribed(userId, true);
      }
      return {
        success: false,
        skipped: true,
        permission: 'granted',
      };
    }

    if (!subRes.ok || !subResult.success) {
      return {
        success: false,
        permission: 'granted',
        error: subResult?.error || 'Failed to save push subscription on server.',
      };
    }

    const updatedStatus = await fetchUserPushStatus(userId);
    emitPushStatusUpdated(userId);
    return {
      success: true,
      permission: 'granted',
      endpoint: subJson.endpoint,
      status: updatedStatus,
    };
  } catch (err: any) {
    return {
      success: false,
      permission: getBrowserPushPermissionState(),
      error: err?.message || 'Failed to subscribe browser to Web Push.',
    };
  }
}

/**
 * Automatically runs after customer or professional login/registration:
 * 1. Registers the Service Worker (/sw.js).
 * 2. Respects disabled push preferences and manual unsubscribes.
 * 3. If browser permission is already 'granted', automatically subscribes/refreshes the device.
 * 4. If permission is 'default' (or iPhone not yet installed to Home Screen), signals the UI to show
 *    a friendly Enable Notifications prompt without requesting permission prematurely.
 */
export async function syncOrAutoSubscribePushOnLogin(params: {
  userId: string;
  role: 'customer' | 'professional';
  email?: string;
}): Promise<{
  action:
    | 'auto_subscribed'
    | 'show_prompt'
    | 'needs_ios_install'
    | 'respect_disabled'
    | 'permission_denied'
    | 'dismissed'
    | 'unsupported';
  permission: BrowserPushPermissionState;
  status?: UserPushStatusResponse | null;
}> {
  const { userId, role, email } = params;
  if (!userId) {
    return { action: 'unsupported', permission: 'unsupported' };
  }

  try {
    // Check iPhone Safari / non-standalone Home Screen requirement first (iOS 16.4+)
    if (isIosDevice() && !isStandaloneWebApp()) {
      if (isPushPromptDismissed(userId) || isLocalPushPreferenceDisabled(userId)) {
        return { action: 'dismissed', permission: getBrowserPushPermissionState() };
      }
      return {
        action: 'needs_ios_install',
        permission: getBrowserPushPermissionState(),
      };
    }

    if (!isWebPushSupportedInBrowser()) {
      return { action: 'unsupported', permission: 'unsupported' };
    }

    // 1. Register the Service Worker automatically on login/registration
    await ensurePushServiceWorkerRegistered();

    // 2. Fetch server-side preference and subscription status
    const status = await fetchUserPushStatus(userId);
    if (status) {
      if (status.pushEnabled === false) {
        setLocalPushPreferenceDisabled(userId, true);
      }
      if (status.manuallyUnsubscribed === true) {
        setDeviceManuallyUnsubscribed(userId, true);
      }
    }

    // 3. Respect disabled preferences and manual unsubscribes
    if (
      status?.pushEnabled === false ||
      status?.manuallyUnsubscribed === true ||
      isLocalPushPreferenceDisabled(userId) ||
      isDeviceManuallyUnsubscribed(userId)
    ) {
      return {
        action: 'respect_disabled',
        permission: getBrowserPushPermissionState(),
        status,
      };
    }

    const permission = getBrowserPushPermissionState();

    // 4. If browser permission is already granted, automatically subscribe or refresh subscription
    if (permission === 'granted') {
      const subRes = await subscribeCurrentDeviceToPush({
        userId,
        role,
        email,
        autoRefresh: true,
      });
      if (subRes.skipped) {
        return {
          action: 'respect_disabled',
          permission: 'granted',
          status,
        };
      }
      return {
        action: 'auto_subscribed',
        permission: 'granted',
        status: subRes.status ?? status,
      };
    }

    // 5. If permission is default, show the friendly Enable Notifications prompt (unless dismissed)
    if (permission === 'default') {
      if (isPushPromptDismissed(userId)) {
        return { action: 'dismissed', permission: 'default', status };
      }
      return {
        action: 'show_prompt',
        permission: 'default',
        status,
      };
    }

    return {
      action: 'permission_denied',
      permission: 'denied',
      status,
    };
  } catch {
    // Non-blocking: never interrupt authentication or app flow
    return {
      action: 'unsupported',
      permission: getBrowserPushPermissionState(),
    };
  }
}

/**
 * Unsubscribes the current browser/device from Web Push and removes its endpoint from the backend.
 */
export async function unsubscribeCurrentDeviceFromPush(userId: string): Promise<{
  success: boolean;
  status?: UserPushStatusResponse | null;
  error?: string;
}> {
  if (!userId) {
    return { success: false, error: 'User ID is required.' };
  }

  // Record manual unsubscribe so automatic login refresh respects it
  setDeviceManuallyUnsubscribed(userId, true);

  let endpoint: string | undefined;
  try {
    if (isWebPushSupportedInBrowser()) {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const sub = await registration?.pushManager?.getSubscription();
      if (sub) {
        endpoint = sub.endpoint;
        await sub.unsubscribe().catch(() => {});
      }
    }
  } catch {
    // Continue to backend cleanup
  }

  try {
    const headers = await getAuthHeaders(userId);
    const res = await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId,
        endpoint,
        allDevices: !endpoint,
      }),
    });
    const data = await res.json();
    const updatedStatus = await fetchUserPushStatus(userId);
    emitPushStatusUpdated(userId);
    return {
      success: Boolean(res.ok && data.success),
      status: updatedStatus,
      error: data.error,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Failed to unsubscribe device.',
    };
  }
}

/**
 * Removes a specific registered device by ID from the user's account.
 */
export async function removeRegisteredPushDevice(
  userId: string,
  deviceId: string
): Promise<{
  success: boolean;
  status?: UserPushStatusResponse | null;
  error?: string;
}> {
  if (!userId || !deviceId) {
    return { success: false, error: 'User ID and device ID are required.' };
  }
  try {
    const headers = await getAuthHeaders(userId);
    const res = await fetch(
      `/api/push/devices/${encodeURIComponent(deviceId)}?userId=${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    const data = await res.json();
    const updatedStatus = await fetchUserPushStatus(userId);
    emitPushStatusUpdated(userId);
    return {
      success: Boolean(res.ok && data.success),
      status: updatedStatus,
      error: data.error,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Failed to remove device.',
    };
  }
}

/**
 * Updates the user's account-level push_enabled preference.
 */
export async function toggleUserPushPreference(params: {
  userId: string;
  role: 'customer' | 'professional';
  pushEnabled: boolean;
}): Promise<{
  success: boolean;
  pushEnabled: boolean;
  error?: string;
}> {
  setLocalPushPreferenceDisabled(params.userId, !params.pushEnabled);
  try {
    const headers = await getAuthHeaders(params.userId);
    const res = await fetch('/api/push/preferences', {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    const data = await res.json();
    const nextEnabled = Boolean(data.pushEnabled ?? params.pushEnabled);
    setLocalPushPreferenceDisabled(params.userId, !nextEnabled);
    emitPushStatusUpdated(params.userId);
    return {
      success: Boolean(res.ok && data.success),
      pushEnabled: nextEnabled,
      error: data.error,
    };
  } catch (err: any) {
    return {
      success: false,
      pushEnabled: params.pushEnabled,
      error: err?.message || 'Failed to update push preference.',
    };
  }
}

/**
 * Dispatches a real Web Push test notification to all active devices registered to the user.
 */
export async function sendUserTestPushNotification(params: {
  userId: string;
  role: 'customer' | 'professional';
}): Promise<{
  success: boolean;
  deliveredCount: number;
  attemptedCount: number;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders(params.userId);
    const res = await fetch('/api/push/test', {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    const data = await res.json();
    return {
      success: Boolean(res.ok && data.success),
      deliveredCount: Number(data.deliveredCount || 0),
      attemptedCount: Number(data.attemptedCount || 0),
      message: data.message,
      error: data.error,
    };
  } catch (err: any) {
    return {
      success: false,
      deliveredCount: 0,
      attemptedCount: 0,
      error: err?.message || 'Failed to send test push notification.',
    };
  }
}
